<script lang="ts">
  // The pick list inside a tag dropdown: one row per tag, with its colour and a tick if selected,
  // toggled by clicking. Shared by CustomActivityTags ("+" on an activity) and the dashboard's
  // ActivityTagFilter. Pass onDelete to also offer a trash button on the athlete's custom tags.
  import { Button } from '@/lib/components/ui/button';

  export let tags: { name: string, color?: string }[];
  export let selected: string[];
  // Shown as a dash rather than a tick: on some but not all of several activities (bulk editing).
  export let partial: string[] = [];
  export let onToggle: (tag: string) => void;
  // Rows that are inert, e.g. a tag whose previous toggle is still saving.
  export let disabledTags: string[] = [];
  // Unselected rows inert -- selected ones can still be toggled off (e.g. at the per-activity limit).
  export let disabledToAdd = false;
  // Names that may be deleted (the athlete's custom tags); ignored without onDelete.
  export let deletable: string[] = [];
  export let onDelete: ((tag: string) => Promise<void>) | undefined = undefined;

  // The custom tag whose row is currently asking "delete this?", if any. Confirmed inline rather
  // than with a ConfirmationPopover, which would be a popover nested in the dropdown.
  let confirmingDelete: string | null = null;
  let deleting = false;

  async function confirmDelete(tag: string) {
    if (!onDelete || deleting) return;
    deleting = true;
    try {
      await onDelete(tag);
      confirmingDelete = null;
    } catch {
      // onDelete reports its own failure; leave the row asking so it can be retried.
    } finally {
      deleting = false;
    }
  }
</script>

<div class="flex flex-col max-h-48 overflow-y-auto">
  {#each tags as tag (tag.name)}
    {#if confirmingDelete === tag.name}
      <div class="flex flex-col gap-1 rounded bg-accent px-2 py-1.5 text-sm">
        <span>Delete "{tag.name}"? Activities already tagged with it keep it.</span>
        <div class="flex flex-row gap-1">
          <Button size="sm" variant="outline" class="h-7 flex-1 px-2" on:click={() => (confirmingDelete = null)}>Cancel</Button>
          <Button size="sm" variant="destructive" class="h-7 flex-1 px-2" disabled={deleting} on:click={() => confirmDelete(tag.name)}>Delete</Button>
        </div>
      </div>
    {:else}
      <div class="flex flex-row items-center rounded hover:bg-accent">
        <button
          type="button"
          class="flex flex-1 min-w-0 flex-row items-center gap-2 px-2 py-1 text-left text-sm disabled:opacity-50"
          disabled={disabledTags.includes(tag.name) || (disabledToAdd && !selected.includes(tag.name))}
          on:click={() => onToggle(tag.name)}
        >
          <span
            class="h-2.5 w-2.5 shrink-0 rounded-full"
            style={`background-color: ${tag.color ?? 'rgb(203 213 225)'}`}
            aria-hidden="true"
          ></span>
          <span class="flex-1 truncate">{tag.name}</span>
          {#if selected.includes(tag.name)}
            <i class="fa-solid fa-check text-xs" aria-label="Selected"></i>
          {:else if partial.includes(tag.name)}
            <i class="fa-solid fa-minus text-xs" aria-label="On some"></i>
          {/if}
        </button>
        {#if onDelete && deletable.includes(tag.name)}
          <button
            type="button"
            class="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-60 hover:bg-black/10 hover:opacity-100"
            title="Delete this tag"
            on:click={() => (confirmingDelete = tag.name)}
          >
            <i class="fa-solid fa-trash-can text-[11px]" aria-hidden="true"></i>
          </button>
        {/if}
      </div>
    {/if}
  {/each}
</div>
